/**
 * Administración → Repositorios → Perfiles de Redes Sociales: elige qué perfiles y cuentas de
 * los archivos de Drive se muestran en el módulo Redes Sociales. Los perfiles nuevos aparecen
 * visibles hasta que se ocultan; la lista de ocultos se guarda en `repo_settings/social` y la
 * aplica el backend a todos los usuarios.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ink, studio, SearchField, FilterPill, PermSwitch } from './studioUi';
import { backendFetch, engineErrorMessage } from './widget/engineConfig';
import NetworkDot from '../repos/NetworkDot';
import { NETWORK_ORDER, formatNumber } from '../repos/socialRepoUtils';
import { relativeTime } from './widget/CreatorHome';

const REPO = 'social';

const SocialProfilesAdmin = () => {
    const [state, setState] = useState({ loading: true, error: '', profiles: [], updatedAt: null, updatedByName: '' });
    const [hidden, setHidden] = useState(new Set());
    const [savedHidden, setSavedHidden] = useState(new Set());
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    const load = async () => {
        setState(prev => ({ ...prev, loading: true, error: '' }));
        try {
            const res = await backendFetch(`/api/repos/${REPO}/settings`);
            if (!res.ok) throw new Error(await engineErrorMessage(res, 'No se pudo leer la configuración.'));
            const data = await res.json();
            const set = new Set(data.hiddenProfiles || []);
            setHidden(set);
            setSavedHidden(new Set(set));
            setState({ loading: false, error: '', profiles: data.profiles || [], updatedAt: data.updatedAt, updatedByName: data.updatedByName });
        } catch (err) {
            setState(prev => ({ ...prev, loading: false, error: err.message }));
        }
    };

    useEffect(() => { load(); }, []);

    /* Perfiles ocultos que ya no están en los archivos siguen en la lista para poder quitarlos. */
    const allProfiles = useMemo(() => {
        const known = new Set(state.profiles.map(p => p.key));
        const orphans = Array.from(savedHidden)
            .filter(key => !known.has(key))
            .map((key) => {
                const [red, ...rest] = key.split('|');
                return { key, red, perfil: rest.join('|'), posts: 0, followers: null, orphan: true };
            });
        return [...state.profiles, ...orphans];
    }, [state.profiles, savedHidden]);

    const groups = useMemo(() => {
        const term = search.trim().toLowerCase();
        const filtered = allProfiles.filter((p) => {
            if (term && !`${p.perfil} ${p.red}`.toLowerCase().includes(term)) return false;
            if (status === 'visible' && hidden.has(p.key)) return false;
            if (status === 'hidden' && !hidden.has(p.key)) return false;
            return true;
        });
        const byRed = new Map();
        filtered.forEach((p) => {
            if (!byRed.has(p.red)) byRed.set(p.red, []);
            byRed.get(p.red).push(p);
        });
        const rank = red => (NETWORK_ORDER.includes(red) ? NETWORK_ORDER.indexOf(red) : NETWORK_ORDER.length);
        return Array.from(byRed.entries())
            .sort((a, b) => rank(a[0]) - rank(b[0]))
            .map(([red, list]) => ({ red, list: list.sort((a, b) => a.perfil.localeCompare(b.perfil, 'es')) }));
    }, [allProfiles, search, status, hidden]);

    const dirty = hidden.size !== savedHidden.size || Array.from(hidden).some(k => !savedHidden.has(k));
    const visibleCount = allProfiles.filter(p => !p.orphan && !hidden.has(p.key)).length;
    const hiddenCount = allProfiles.filter(p => hidden.has(p.key)).length;

    const toggle = (key, visible) => {
        setMessage('');
        setHidden((prev) => {
            const next = new Set(prev);
            if (visible) next.delete(key);
            else next.add(key);
            return next;
        });
    };
    const setGroup = (list, visible) => {
        setMessage('');
        setHidden((prev) => {
            const next = new Set(prev);
            list.forEach(p => (visible ? next.delete(p.key) : next.add(p.key)));
            return next;
        });
    };

    const save = async () => {
        setSaving(true);
        setMessage('');
        try {
            const res = await backendFetch(`/api/repos/${REPO}/settings`, {
                method: 'PUT',
                body: JSON.stringify({ hiddenProfiles: Array.from(hidden) }),
            });
            if (!res.ok) throw new Error(await engineErrorMessage(res, 'No se pudo guardar.'));
            await load();
            setMessage('Guardado. El módulo Redes Sociales ya muestra solo los perfiles visibles (puede tardar hasta 30 s).');
        } catch (err) {
            setMessage(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={studio.page}>
            <header style={studio.header}>
                <div>
                    <p style={studio.kicker}>Repositorios</p>
                    <h1 style={studio.title}>Perfiles de Redes Sociales</h1>
                    <p style={studio.subtitle}>
                        Elige qué perfiles y cuentas de los archivos de Drive aparecen en el módulo Redes Sociales.
                        Los ocultos no se muestran a nadie ni entran en ningún cálculo. Los perfiles nuevos aparecen
                        visibles hasta que los ocultes.
                    </p>
                </div>
                <div style={studio.stats}>
                    <div style={studio.stat}><span style={studio.statValue}>{visibleCount}</span><span style={studio.statLabel}>Visibles</span></div>
                    <div style={{ ...studio.stat, borderLeftColor: ink.faint }}><span style={studio.statValue}>{hiddenCount}</span><span style={studio.statLabel}>Ocultos</span></div>
                </div>
            </header>

            <div style={styles.toolbar}>
                <SearchField value={search} onChange={setSearch} placeholder="Buscar perfil o red…" style={{ marginBottom: 0, flex: '1 1 240px' }} />
                <FilterPill value={status} onChange={setStatus} style={{ minWidth: 160 }}>
                    <option value="">Todos</option>
                    <option value="visible">Solo visibles</option>
                    <option value="hidden">Solo ocultos</option>
                </FilterPill>
                <button type="button" onClick={save} disabled={!dirty || saving} style={{ ...studio.primaryBtn, opacity: !dirty || saving ? 0.6 : 1 }}>
                    {saving ? 'Guardando…' : 'Guardar cambios'}
                </button>
                {dirty && !saving && (
                    <button type="button" style={studio.ghostBtn} onClick={() => { setHidden(new Set(savedHidden)); setMessage(''); }}>Descartar</button>
                )}
            </div>
            <p style={studio.hint}>
                {state.updatedAt
                    ? `Última modificación ${relativeTime(state.updatedAt)}${state.updatedByName ? ` por ${state.updatedByName}` : ''}.`
                    : 'Aún no se ha ocultado ningún perfil.'}
            </p>
            {message && <div style={styles.notice}>{message}</div>}
            {state.error && (
                <div style={styles.error}>
                    {state.error}
                    <button type="button" onClick={load} style={{ ...studio.ghostBtn, marginLeft: 12, padding: '6px 12px' }}>Reintentar</button>
                </div>
            )}
            {state.loading && <p style={studio.hint}>Leyendo los perfiles de los archivos de Drive…</p>}

            <div style={styles.grid}>
                {groups.map(({ red, list }) => {
                    const visibleInGroup = list.filter(p => !hidden.has(p.key)).length;
                    return (
                        <div key={red} style={studio.permCard}>
                            <div style={studio.permCardHead}>
                                <h5 style={{ ...studio.permCardTitle, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <NetworkDot red={red} size={10} />{red}
                                </h5>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={studio.tabBadge}>{visibleInGroup}/{list.length}</span>
                                    <button type="button" style={studio.filterClearAll} onClick={() => setGroup(list, visibleInGroup !== list.length)}>
                                        {visibleInGroup === list.length ? 'Ocultar todos' : 'Mostrar todos'}
                                    </button>
                                </div>
                            </div>
                            {list.map(p => (
                                <PermSwitch
                                    key={p.key}
                                    checked={!hidden.has(p.key)}
                                    onChange={visible => toggle(p.key, visible)}
                                    label={(
                                        <span style={styles.profileLabel}>
                                            <span style={styles.profileName}>{p.perfil}</span>
                                            <span style={styles.profileMeta}>
                                                {p.orphan
                                                    ? 'Ya no aparece en los archivos'
                                                    : `${formatNumber(p.posts)} posts${p.followers != null ? ` · ${formatNumber(p.followers)} seguidores` : ''}`}
                                            </span>
                                        </span>
                                    )}
                                />
                            ))}
                        </div>
                    );
                })}
                {!state.loading && !groups.length && !state.error && <p style={studio.hint}>No hay perfiles con estos filtros.</p>}
            </div>
        </div>
    );
};

const styles = {
    toolbar: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 },
    profileLabel: { display: 'flex', flexDirection: 'column', minWidth: 0 },
    profileName: { fontWeight: 600, color: ink.text, overflow: 'hidden', textOverflow: 'ellipsis' },
    profileMeta: { fontSize: '0.74rem', color: ink.faint },
    notice: { padding: '10px 12px', borderRadius: 10, backgroundColor: '#e6f4ea', color: '#137333', fontSize: '0.86rem' },
    error: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', padding: '12px 14px', borderRadius: 12, backgroundColor: ink.dangerSoft, color: ink.danger, fontSize: '0.88rem' },
};

export default SocialProfilesAdmin;
